import {
    DalItem,
    ValidateValue,
    pointerUtils,
    type CreateExtArgs,
    type DmApis,
    type Extension,
    type ExtensionAPI
} from '@wix/document-manager-core'
import type {BuilderStyle, CompRef, DesignObject, Pointer, StyleRef} from '@wix/document-services-types'
import _ from 'lodash'
import type {PageExtensionAPI} from '../page'
import {fromStylableColor, regularColorRegExp, stylableColorsRegExp, toStylableColor} from '../../utils/colors'
import {deepFind} from '../../utils/object'
import {
    DATA_TYPES,
    GLOBAL_STYLES,
    MASTER_PAGE_ID,
    PAGE_NUMBER_LIMIT_FOR_STYLE_PER_PAGE,
    STYLES,
    VIEW_MODES
} from '../../constants/constants'
import {createMemo} from '../../utils/memoize'
import type {RMApi} from '../rendererModel'
import type {DataModelExtensionAPI} from '../dataModel/dataModel'
import {ReportableError} from '@wix/document-manager-utils'
import {addHash, refArray, variantRelation} from '../../utils/refArrayUtils'
import {isStructurePointer} from '../structure'
import {deepClone} from '@wix/wix-immutable-proxy'
import type {RelationshipsAPI} from '../relationships'
import type {CssStyleExtensionAPI} from '../cssStyle/cssStyle'

interface StylesApi extends ExtensionAPI {
    replaceColorInAllItems(
        oldColor: string,
        newColor: string,
        pageId: string,
        predicate?: (pointer: Pointer) => boolean
    ): void
    getColorsListInAllComponents(): string[]
    isStylePerPageEnabled(): boolean
    getComponentsUsingStyle(stylePointer: Pointer): CompRef[]
}

interface GlobalStyleApi extends ExtensionAPI {
    get(globalStylePointer: Pointer): BuilderStyle | undefined
    getAll(): BuilderStyle[]
    getNewGlobalStylePointer(customGlobalStyleId?: string): Pointer
    create(newGlobalStyleId: string, globalStyleItem: Partial<BuilderStyle>): Pointer
    update(
        globalStylePointer: Pointer,
        globalStyleItem: Partial<BuilderStyle>,
        shouldRemoveRelatedProperties?: boolean
    ): void
    remove(globalStylePointer: Pointer, paths: string[]): void
    delete(globalStylePointer: Pointer): void
    replace(oldGlobalStylePointer: Pointer, newGlobalStylePointer: Pointer): void
    getGlobalStylesList(): string[]
    isGlobalStyle(pointerOrId: Pointer | string): boolean
}

export interface StylesExtensionApi extends ExtensionAPI {
    style: StylesApi & {
        global: GlobalStyleApi
    }
}

const createExtension = (): Extension => {
    const createExtensionAPI = ({extensionAPI, pointers, dal}: CreateExtArgs): StylesExtensionApi => {
        const {dataModel, page, rendererModel, cssStyle} = extensionAPI as DataModelExtensionAPI &
            PageExtensionAPI &
            RMApi &
            CssStyleExtensionAPI
        const {memoize} = createMemo()

        const getColorsListInAllComponents = () => {
            const usedColors = new Set<string>()

            const checkAndCollectColors = (item: StyleRef | DesignObject): boolean => {
                const colors: string[] = []
                _.cloneDeepWith(item, value => {
                    if (typeof value !== 'string') {
                        return
                    }
                    const matched = value.match(regularColorRegExp)
                    if (matched) colors.push(...matched)
                })

                colors?.forEach(colorId => usedColors.add(colorId))
                return colors.length > 0
            }

            const checkAndCollectStylableColors = (item: StyleRef) => {
                const stcss: string | undefined = item.style?.properties?.['$st-css']
                const matched = stcss?.match(stylableColorsRegExp)
                matched
                    ?.map(colorId => fromStylableColor(colorId))
                    .filter((colorId): colorId is string => !!colorId)
                    .forEach(colorId => usedColors.add(colorId))
                return matched
            }

            const pageIds = page.getAllPagesIds(true)

            pageIds.forEach(pageId => {
                pointers.data.getStyleItemsWithPredicate(
                    (styleObj: StyleRef) => styleObj.type === 'ComponentStyle' && checkAndCollectColors(styleObj),
                    pageId
                )
                pointers.data.getStyleItemsWithPredicate(checkAndCollectStylableColors, pageId)
                pointers.data.getDesignItemsWithPredicate(
                    (designObj: DesignObject) => checkAndCollectColors(designObj),
                    pageId
                )
            })

            return Array.from(usedColors)
        }

        const replaceColorInAllItems = (
            oldColor: string,
            newColor: string,
            pageId: string,
            predicate?: (pointer: Pointer) => boolean
        ) => {
            const regularRegExp = new RegExp(`${oldColor}(?!\\d)`, 'g')
            const oldStylableColor = toStylableColor(oldColor)
            const newStylableColor = toStylableColor(newColor)
            const stylableRegExp = oldStylableColor && new RegExp(`${oldStylableColor}`, 'g')

            const replaceRegularColors = (pointer: Pointer) => {
                const styleData = dal.get(pointer)
                const updatedStyleData = _.cloneDeepWith(styleData, value =>
                    typeof value === 'string' ? value.replace(regularRegExp, newColor) : undefined
                )
                dal.set(pointer, updatedStyleData)
            }

            const replacerStylableColors = (pointer: Pointer) => {
                const stcssPointer = pointers.getInnerPointer(pointer, ['style', 'properties', '$st-css'])
                const stcss: string = dal.get(stcssPointer)
                if (!stcss || !stylableRegExp || !newStylableColor) {
                    return
                }
                const updatedStCss = stcss.replace(stylableRegExp, newStylableColor)
                dal.set(stcssPointer, updatedStCss)
            }

            if (stylableRegExp) {
                const stylableItemWithPointers = pointers.data.getStyleItemsWithPredicate(
                    itemData => itemData.style?.properties?.['$st-css']?.match(stylableRegExp),
                    pageId
                )
                // replace in stylables
                stylableItemWithPointers
                    ?.filter(pointer => !predicate || predicate(pointer))
                    .forEach(pointer => replacerStylableColors(pointer))
            }

            const styleItemWithPointers = pointers.data.getStyleItemsWithPredicate(
                styleDalItem => deepFind(styleDalItem, value => typeof value === 'string' && regularRegExp.test(value)),
                pageId
            )
            // replace in styleItems
            styleItemWithPointers
                .filter(pointer => !predicate || predicate(pointer))
                .forEach(pointer => replaceRegularColors(pointer))

            const designItemsPointers = pointers.data.getDesignItemsWithPredicate(
                designDalItem =>
                    deepFind(designDalItem, value => typeof value === 'string' && regularRegExp.test(value)),
                pageId
            )
            // replace in desingItems
            designItemsPointers
                .filter(pointer => !predicate || predicate(pointer))
                .forEach(pointer => replaceRegularColors(pointer))
        }

        const isStylePerPageEnabled = () => {
            const stylesPerPage = dal.get({
                type: DATA_TYPES.data,
                id: MASTER_PAGE_ID,
                innerPath: ['styleSettings', 'stylesPerPage']
            })
            const initialPages = rendererModel.getInitialPageList()?.pages ?? []
            const initialNumPages = initialPages.length

            if (stylesPerPage && parseFloat(stylesPerPage) >= parseFloat(STYLES.STYLES_PER_PAGE_VER)) {
                return true
            }

            return initialNumPages <= PAGE_NUMBER_LIMIT_FOR_STYLE_PER_PAGE
        }

        const getGlobalStyleById = (globalStyleId: string) => {
            return dataModel.getItem(globalStyleId, DATA_TYPES.theme, MASTER_PAGE_ID)
        }

        const getGlobalStyleByPointer = (globalStylePointer: Pointer): BuilderStyle | undefined => {
            if (dal.has(globalStylePointer)) {
                return cssStyle.getPublicCSSStyle(
                    dataModel.getItem(globalStylePointer.id, DATA_TYPES.theme, MASTER_PAGE_ID)
                )
            }
        }

        const globalStylesArrayPointer = pointerUtils.getInnerPointer(
            pointers.data.getThemeItem(GLOBAL_STYLES, MASTER_PAGE_ID),
            'styles'
        )
        const getAllGlobalStyles = () => {
            const globalStylesArray = dataModel.getItem(GLOBAL_STYLES, DATA_TYPES.theme, MASTER_PAGE_ID)
            if (globalStylesArray) {
                return deepClone(globalStylesArray).styles || []
            }
            return []
        }
        const getNewGlobalStylePointer = (customGlobalStyleId?: string) => {
            const newGlobalStyleId = customGlobalStyleId ?? dataModel.generateItemIdWithPrefix('global')

            if (getGlobalStyleById(newGlobalStyleId)) {
                throw new ReportableError({
                    errorType: 'GLOBAL_STYLE_ALREADY_EXIST',
                    message: `Global style with id ${newGlobalStyleId} already exists.`,
                    extras: {
                        globalStyleId: newGlobalStyleId
                    }
                })
            }

            return pointers.data.getThemeItem(newGlobalStyleId, MASTER_PAGE_ID)
        }

        const createGlobalStyle = (newGlobalStyleId: string, globalStyleItem: BuilderStyle) => {
            const dataItem = dataModel.createStyleItemByType(globalStyleItem.type!)

            if (!dataItem) {
                throw new ReportableError({
                    errorType: 'GLOBAL_STYLE_SCHEMA_NOT_FOUND',
                    message: `Global style schema not found for type ${globalStyleItem.type}.`,
                    extras: {
                        globalStyleId: newGlobalStyleId
                    }
                })
            }

            const newGlobalStyleItem = {...dataItem, ...globalStyleItem, id: newGlobalStyleId}

            const globalStylesCollectionPointer = pointers.data.getThemeItem(GLOBAL_STYLES, MASTER_PAGE_ID)

            if (dal.has(globalStylesCollectionPointer)) {
                dataModel.addItem(newGlobalStyleItem, DATA_TYPES.theme, MASTER_PAGE_ID, newGlobalStyleId)

                const globalStylesArray = dal.get(globalStylesArrayPointer)

                dal.set(globalStylesArrayPointer, globalStylesArray.concat(addHash(newGlobalStyleId)))
            } else {
                const globalStyleCollection = dataModel.createStyleItemByType('GlobalStylesCollection') as any

                globalStyleCollection.styles = [newGlobalStyleItem]

                dataModel.addItem(globalStyleCollection, DATA_TYPES.theme, MASTER_PAGE_ID, GLOBAL_STYLES)
            }

            return pointers.data.getThemeItem(newGlobalStyleId, MASTER_PAGE_ID)
        }

        const getGlobalStylesList = (): string[] => {
            const globalStylesArray = dal.get(globalStylesArrayPointer)

            return globalStylesArray ?? []
        }

        const isGlobalStyle = (pointerOrId: Pointer | string): boolean => {
            const globalStylesList = getGlobalStylesList()
            const globalStyleId = typeof pointerOrId === 'string' ? pointerOrId : pointerOrId.id

            return globalStylesList.includes(addHash(globalStyleId)!)
        }

        const update = (
            globalStylePointer: Pointer,
            partialStyle: BuilderStyle,
            shouldRemoveRelatedProperties: boolean = true
        ) => {
            const styleItem = dataModel.getItem(globalStylePointer.id, DATA_TYPES.theme, MASTER_PAGE_ID)
            const styleToUpdate = cssStyle.getUpdatedCSSStyleItemForUpdate(
                styleItem,
                partialStyle,
                shouldRemoveRelatedProperties
            )
            return dataModel.addItem(styleToUpdate, DATA_TYPES.theme, MASTER_PAGE_ID, globalStylePointer.id)
        }

        const deleteStyle = (globalStylePointer: Pointer) => {
            const {relationships} = extensionAPI as RelationshipsAPI

            const componentsUsingStyle = relationships.getOwningComponentsToPointer(globalStylePointer)

            if (componentsUsingStyle.length > 0) {
                throw new ReportableError({
                    errorType: 'GLOBAL_STYLE_IN_USE',
                    message: `Cannot remove global style ${globalStylePointer.id} because it is used by components.`,
                    extras: {
                        globalStyleId: globalStylePointer.id,
                        componentsUsingStyle
                    }
                })
            }

            const globalStylesList = getGlobalStylesList()

            const updatedGlobalStylesList = globalStylesList.filter(
                styleId => styleId !== addHash(globalStylePointer.id)
            )

            if (updatedGlobalStylesList.length === globalStylesList.length) {
                return
            }

            dal.set(globalStylesArrayPointer, updatedGlobalStylesList)

            dataModel.removeItemRecursively(globalStylePointer)
        }
        const remove = (globalStylePointer: Pointer, paths: string[]) => {
            const styleItem = dataModel.getItem(globalStylePointer.id, DATA_TYPES.theme, MASTER_PAGE_ID)
            const styleToUpdate = cssStyle.getUpdatedCSSStyleItemForRemove(styleItem, paths)
            if (styleToUpdate) {
                dataModel.addItem(styleToUpdate, DATA_TYPES.theme, MASTER_PAGE_ID, globalStylePointer.id)
            }
        }
        const getComponentsUsingStyle = (globalStylePointer: Pointer): CompRef[] => {
            const {relationships} = extensionAPI as RelationshipsAPI

            return relationships.getOwningComponentsToPointer(globalStylePointer) as CompRef[]
        }

        const replace = (oldGlobalStylePointer: Pointer, newGlobalStylePointer: Pointer) => {
            const {relationships} = extensionAPI as RelationshipsAPI

            const owningReferences = relationships.getOwningReferencesToPointer(oldGlobalStylePointer)

            owningReferences.forEach((owningReference: Pointer) => {
                if (owningReference.id === GLOBAL_STYLES) {
                    return
                }

                const dataItem = deepClone(dal.get(owningReference))

                if (variantRelation.isVariantRelation(dataItem) && dataItem.to === `#${oldGlobalStylePointer.id}`) {
                    dataItem.to = `#${newGlobalStylePointer.id}`
                } else if (refArray.isRefArray(dataItem)) {
                    dataItem.values = dataItem.values.map((value: string) => {
                        if (value === `#${oldGlobalStylePointer.id}`) {
                            return `#${newGlobalStylePointer.id}`
                        }
                        return value
                    })
                } else if (dataItem.styleId === oldGlobalStylePointer.id) {
                    dataItem.styleId = newGlobalStylePointer.id
                }

                dal.set(owningReference, dataItem)
            })
        }

        return {
            style: {
                isStylePerPageEnabled: memoize<StylesApi['isStylePerPageEnabled']>(isStylePerPageEnabled),
                replaceColorInAllItems,
                getColorsListInAllComponents,
                getComponentsUsingStyle,
                global: {
                    get: getGlobalStyleByPointer,
                    getAll: getAllGlobalStyles,
                    getNewGlobalStylePointer,
                    create: createGlobalStyle,
                    update,
                    remove,
                    delete: deleteStyle,
                    getGlobalStylesList,
                    replace,
                    isGlobalStyle
                }
            }
        }
    }

    const createPublicAPI = ({extensionAPI}: DmApis) => {
        const {style} = extensionAPI as StylesExtensionApi

        return {
            style: {
                getComponentsUsingStyle: (stylePointer: Pointer) => style.getComponentsUsingStyle(stylePointer),
                /**
                 * The function replaces all the occurrences of colorId in style objects and design items
                 * @param {oldColor} color that needs to be replaced
                 * @param {newColor} color that replaces an old color
                 */
                replaceColorInAllComponents: style.replaceColorInAllItems,
                /**
                 * The function returns all colors that are used in site components
                 */
                getColorsListInAllComponents: style.getColorsListInAllComponents,
                global: {
                    create: (newGlobalStylePointer: Pointer, globalStyleItem: BuilderStyle) =>
                        style.global.create(newGlobalStylePointer.id, globalStyleItem),
                    update: (
                        globalStylePointer: Pointer,
                        globalStyleItem: BuilderStyle,
                        shouldRemoveRelatedProperties?: boolean
                    ) => style.global.update(globalStylePointer, globalStyleItem, shouldRemoveRelatedProperties),
                    remove: (globalStylePointer: Pointer, paths: string[]) =>
                        style.global.remove(globalStylePointer, paths),
                    delete: (globalStylePointer: Pointer) => style.global.delete(globalStylePointer),
                    isGlobalStyle: (pointerOrId: Pointer | string) => style.global.isGlobalStyle(pointerOrId),
                    getNewGlobalStylePointer: (globalStyleItem: BuilderStyle, customGlobalStyleId?: string) =>
                        style.global.getNewGlobalStylePointer(customGlobalStyleId),
                    replace: (oldGlobalStylePointer: Pointer, newGlobalStylePointer: Pointer) =>
                        style.global.replace(oldGlobalStylePointer, newGlobalStylePointer),
                    get: (globalStylePointer: Pointer) => style.global.get(globalStylePointer),
                    getAll: () => style.global.getAll()
                }
            }
        }
    }

    const createValidator = ({dal, extensionAPI, pointers}: DmApis): Record<string, ValidateValue> => ({
        validateBuilderComponentStyle: (pointer: Pointer, value: DalItem) => {
            const {style} = extensionAPI as StylesExtensionApi

            const validateStyleTypeAgainstBuilderComponent = (styleId?: string, componentType?: string) => {
                if (!styleId || !componentType || !style.global.isGlobalStyle(styleId)) {
                    return
                }
                const styleItem = dal.get(pointers.data.getThemeItem(styleId))
                if (!styleItem) {
                    return
                }
                if (styleItem.type !== `${componentType}.Style`) {
                    return [
                        {
                            shouldFail: true,
                            type: 'STYLE_TYPE_MISMATCH',
                            message: `The style type ${styleItem.type} is not derived from the component manifest. The component type is ${componentType}`
                        }
                    ]
                }
            }

            if (!value) {
                return
            }

            if (pointer.type === DATA_TYPES.theme && variantRelation.isVariantRelation(value)) {
                const styleId = variantRelation.extractTo(value)
                const componentId = variantRelation.extractFrom(value)
                const component = dal.get(pointerUtils.getPointer(componentId, VIEW_MODES.DESKTOP))

                return validateStyleTypeAgainstBuilderComponent(styleId, component?.componentType)
            } else if (isStructurePointer(pointer)) {
                return validateStyleTypeAgainstBuilderComponent(value.styleId, value.componentType)
            }
        }
    })

    return {
        name: 'style',
        createExtensionAPI,
        createPublicAPI,
        createValidator
    }
}

export {createExtension}
